/* globals showModalError */
import React, { useContext, useEffect, useState } from "react";
import SettingsView from "common/SettingsView";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import ActionsContext from "common/ActionsContext";
import View from "common/View";
import Modal from "common/Modal";
import { ShowManagementBypassSettings } from "views/Config/Interfaces/Bypass/component";
import ActionableSelectionTable from "common/graphs/Table/ActionableSelectionTable";
import help from "help/viewBypass.md";
import withReloading from "common/withReloading";
import Loading from "common/Loading";
import { atBqnTarget } from "common/api";

const CANT_PROCEED_ERROR = `Cannot proceed while there are pending config changes. Try again later.`;

const clear = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

class UserDeclined extends Error { }

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding-top: 5px;
`;

const checkNothingPending = () =>
  ifCl.run("show config diff").then((response) => {
    if (response.length > 0) {
      throw new Error(CANT_PROCEED_ERROR);
    }
  });

const submitCommand = (command) => ifCl.run(command + "\n", /*is batch*/ true);

const clearConfigAttempt = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const composeApplyCommand = (originalSettings, settings) => {
  if (settings.mode === "disabled") {
    return `configure
high-availability
hid normal
hid bypass bqn-id ${settings.bqn}
hid normal bqn-id ${settings.bqn}
no hid normal
no hid bypass bqn-id ${settings.bqn}
no hid normal bqn-id ${settings.bqn}
commit
`;
  } else if (settings.mode === "bypass") {
    return `configure
high-availability
hid normal
hid bypass bqn-id ${settings.bqn}
hid normal bqn-id ${settings.bqn}
no hid normal
no hid normal bqn-id ${settings.bqn}
commit
`;
  } else if (settings.mode === "normal") {
    return `configure
high-availability
hid normal
hid bypass bqn-id ${settings.bqn}
hid normal bqn-id ${settings.bqn}
no hid normal
no hid bypass bqn-id ${settings.bqn}
commit
`;
  }

  return null;
};

const notifyAndRevert = (error) => {
  if (error instanceof UserDeclined) {
    return { error: null };
  }
  error = error.message === undefined ? new Error(error) : error;
  clearConfigAttempt();
  showModalError("Error", error);
};

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.map((item) =>
      composeApplyCommand({}, { mode: "disabled", bqn: item.bqn })
    );
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError("Error", error.message || error));
  };
  if (!showDeleteModal) {
    return null;
  }
  const title =
    items.length === 1
      ? `Disable bypass device${items[0].bqn === "all" || !items[0].bqn
        ? ""
        : ` in BQN ${items[0].bqn}`
      }?`
      : `Disable ${items.length} bypass devices?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length === 1 ? (
            <p><b>WARNING</b>: The bypass device will be disabled. All configuration about the device will be lost and the mode of the device will be undetermined.</p>
          ) : null}
          {items.length > 1 ? (
            <>
              {items.length < 100 ? (
                <ul>{items.map((item, i) => (<li key={`${item.device}-${i}`}>{item.device} {item.bqn && item.bqn !== "all" ? `(BQN ${item.bqn})` : ""}</li>))}</ul>
              ) : null}
              <p><b>WARNING</b>: The bypass devices will be disabled. All configuration about the devices will be lost and the mode of the devices will be undetermined.</p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const ListBypass = ({ outcome: response, mode, bqnId, columns, selected, setSelected, setDeleteSelected, setShowDeleteModal }) => {
  const [header, ...rows] = response.trim().split("\n");
  const headerAttrs = header.trim().split(/\s+/);
  const newRows = rows.map((row) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    return rowAttrs.reduce((acc, attr, index) => {
      acc[headerAttrs[index]] = attr;
      return acc;
    }, {});
  });
  const rowsShown = newRows.map((row, index) => ({
    ...row,
    id: row.id !== undefined ? row.id : index,
  }));

  return (
    <ActionableSelectionTable
      isSelectable={true}
      isRowSelectable={false}
      data={rowsShown}
      sort={true}
      columns={columns}
      search={true}
      csvPrefix="external-bypass-devices"
      rowsPerPageNr={50}
      selected={selected}
      setSelected={setSelected}
      deleteSelected={(values) => {
        const selectedItems = rowsShown
          .filter((row) => values.includes(row.id))
          .map((row) => ({
            device: row["MODEL"],
            bqn: mode === "cluster" ? row["BQN"] : bqnId,
          }));
        setDeleteSelected(selectedItems);
        setShowDeleteModal(true);
      }}
    />
  );
};

const ListBypassWithReloading = withReloading(ListBypass);

const Bypass = ({returnView}) => {
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const actions = useContext(ActionsContext);
  // The devices of the BQN selected in the header, or of the whole cluster.
  // The response keeps its BQN column either way.
  const doRequest = () =>
    ifCl.run(atBqnTarget("show high-availability hid", mode, bqnId));

  useEffect(() => actions.recv("do-load", () => setSelected([])), []);

  useEffect(() => setSelected([]), [bqnId]);

  const apply = (settings, originalSettings = {}) =>
    checkNothingPending()
      .then(() => composeApplyCommand(originalSettings, settings))
      .then(submitCommand)
      .then(() => actions.send('do-load'))
      .catch(notifyAndRevert);

  const getColumns = () => {
    return [
      ...(mode !== "standalone"
        ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
          },
        ]
        : []),
      {
        label: "MODEL",
        id: "MODEL",
        numeric: false,
      },
      {
        label: "SERIAL-NUMBER",
        id: "SERIAL-NUMBER",
        numeric: false,
      },
      {
        label: "MODE",
        id: "MODE",
        numeric: false,
      },
      {
        label: "LINKS-IN-BYPASS",
        id: "LINKS-IN-BYPASS",
        numeric: false,
      },
      {
        label: "ACTIONS",
        id: "ACTIONS",
        sort: false,
        actions: [
          {
            label: "Edit",
            icon: "edit",
            onClick: (row) => {
              setEditing({
                device: row["MODEL"],
                bqn: mode === "cluster" ? row["BQN"] : bqnId,
                mode: row["MODE"],
              });
            },
          },
          {
            label: "Delete",
            icon: "delete",
            onClick: (row) => {
              setDeleteSelected([
                {
                  device: row["MODEL"],
                  bqn: mode === "cluster" ? row["BQN"] : bqnId,
                },
              ]);
              setShowDeleteModal(true);
            },
          },
        ],
      },
    ];
  };

  const columns = getColumns();
  return (
    <>
      <View
        title="External Bypass Devices"
        mdHelp={help}
        returnView={returnView}
        bodyClassName="expect-overflow"
      >
            <TableContainer>
              {/* Nothing is asked while the BQN context has not loaded the
                  BQN selected: the command would not carry its option, and
                  the response would be that of the whole cluster. */}
              {bqnId === undefined ? (
                <Loading />
              ) : (
                <ListBypassWithReloading
                  key={bqnId}
                  provider={doRequest}
                  mode={mode}
                  bqnId={bqnId}
                  columns={columns}
                  selected={selected}
                  setSelected={setSelected}
                  setDeleteSelected={setDeleteSelected}
                  setShowDeleteModal={setShowDeleteModal}
                />
              )}
            </TableContainer>
      </View>
      {editing !== null && (
        <Modal
          id="bypass-edit"
          large={true}
          onClose={() => setEditing(null)}
          noPadding={true}
          content={() => (
            <SettingsView
              className="container-lg"
              load={() =>
                Promise.resolve({
                  ...editing,
                  bqn: editing.bqn,
                  device: editing.device,
                  mode: editing.mode,
                })
              }
              apply={apply}
              clear={clear}
              ContextMenu={() => null}
              title={`External Bypass Device ${editing.device}${mode === "cluster" ? ` in BQN ${editing.bqn}` : ""
                }`}
              applyActionClassName="hidden-to-operators"
              wrapIntoForm={false}
              subtitle={
                <small>
                  Press <code>Apply</code> button at the end of the page to set
                  the new configuration values.
                </small>
              }
              bqnSelector={false}
              noMargin={true}
            >
              <ShowManagementBypassSettings />
            </SettingsView>
          )}
        />
      )}
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          actions.send("do-load");
        }}
      />
    </>
  );
};

export default Bypass;
